import { useSWRMutation } from '@tiger/swr';
import lscache from 'lscache';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { openNativeModal, useAlertModal, useConfirmModal } from 'components/Modal';
import Toast from 'components/Toast';
import { PlaidBankCardItem } from 'components/vendor/plaid';
import { useDeleteBankCard } from 'modules/BankCards/request';
import arrowIcon from 'static/images/icons/icon-arrow.svg';
import APP from 'stores/APP';
import security from 'stores/security';
import { Source } from 'types/funding';
import API from 'utils/API';
import http from 'utils/http';
import AchBankItem from '../../AchBankItem';
import deleteIcon from '../images/delete.svg';
import PlaidRenewModalContent from '../PlaidRenewModalContent';
import './style.scss';

interface AchEnabledBankCardListProps {
    data: PlaidBankCardItem[];
    isModify: boolean;
    source: Source;
    isIRA?: boolean;
    onDelete: () => void;
}

const AchEnabledBankCardList: FC<AchEnabledBankCardListProps> = ({ data, isModify, source, isIRA, onDelete }) => {
    const { open: openAlertModal } = useAlertModal();
    const { open: openConfirmModal } = useConfirmModal();
    const navigate = useNavigate();
    const { trigger } = useSWRMutation('delete.ach.bank.card', (id: PlaidBankCardItem['account_id']) =>
        http.post(
            API.TRADE.funding.deleteCard(),
            {
                plaid_account_id: id,
                customer_id: security.currentCustomerId,
                account_id: security.currentAccountId
            },
            {
                params: {
                    customer_id: security.currentCustomerId,
                    account_id: security.currentAccountId
                }
            }
        )
    );
    const { trigger: deleteBankCardTrigger } = useDeleteBankCard();
    const list = useMemo(() => {
        return data.filter(({ status, id_source }) => {
            /**
             * plaid银行卡全部展示
             * 手动认证银行卡入金展示可用的、审核中；出金展示可用的
             * */
            if (id_source === 'Bank') {
                if (source === 'deposit') {
                    return ['AVAILABLE', 'VERIFIED'].includes(status);
                }

                return ['AVAILABLE'].includes(status);
            }

            return true;
        });
    }, [data, source]);

    const redirectUri = useMemo(() => {
        return `${window.location.origin}/transaction/plaid/${source}${APP.isTiger ? '/inapp' : ''}`;
    }, [source]);

    const handleAchBank = async (item: PlaidBankCardItem) => {
        if (isModify) {
            return;
        }

        if (
            item.expiration_at &&
            item.days_to_expire &&
            (item.expiration_at - Date.now()) / 86400000 <= item.days_to_expire
        ) {
            const cacheKey = `${item.id}-plaid-confirmed`;
            const today = dayjs().format('YYYY-MM-DD');

            if (lscache.get(cacheKey) !== today) {
                lscache.set(cacheKey, today, 1440);

                openNativeModal({
                    maskClosable: true,
                    closable: true,
                    title: __('Account expiration reminder'),
                    component: props => (
                        <PlaidRenewModalContent
                            dayCount={item.days_to_expire!}
                            redirectUri={redirectUri}
                            fundCardId={item.id}
                            close={props.close}
                        />
                    )
                });

                return;
            }
        }

        if (source === 'withdraw' && item.status === 'VERIFIED') {
            openAlertModal({
                content: __(
                    'We need a little more time to bind this bank account. Please try again later or try with another bank account.'
                ),
                keyboard: false,
                centered: true,
                icon: null
            });

            return;
        }

        if (item.status === 'ERROR') {
            openAlertModal({
                content: __('Please hit "Manage Bank Account" to remove the bank account and re-verify it.'),
                keyboard: false,
                centered: true,
                icon: null
            });

            return;
        }

        navigate(`/profile/${source}/ach/fill`, {
            state: item
        });
    };

    const deleteCard = async (item: PlaidBankCardItem) => {
        Toast.loading(true);

        try {
            if (item.id_source === 'Fund') {
                await trigger(item.account_id);
            } else {
                await deleteBankCardTrigger(item.id);
            }

            Toast.loading(false);

            onDelete();
        } catch (err) {
            Toast.loading(false);
            Toast.show(err);
        }
    };

    const handleDelete = (item: PlaidBankCardItem) => {
        openConfirmModal({
            title: __('Delete and revoke account authorization'),
            content: (
                <div>
                    <div>
                        {__(
                            'After deletion, you will not be able to use this account for ACH deposits and withdrawals. Would you like to continue to delete?'
                        )}
                    </div>
                </div>
            ),
            onOk: () => {
                deleteCard(item);
            },
            centered: true,
            okText: __('Confirm')
        });
    };

    const title = useMemo(() => {
        if (isIRA) {
            if (source === 'deposit') {
                return __('ACH Contribution');
            }

            if (source === 'withdraw') {
                return __('ACH Distribution');
            }
        }

        return __('Bank Profiles');
    }, [isIRA, source]);

    return (
        <div className="ach-enabled-bank-card-list">
            <h3 className="main-title">{title}</h3>
            <div className="bank-card-list">
                {list.map(item => {
                    const { id } = item;

                    return (
                        <div
                            key={id}
                            className="ach-bank-item"
                            onClick={() => {
                                handleAchBank(item);
                            }}>
                            <AchBankItem info={item} showStatusTag />
                            {/**
                             * 账户处于审核中，不可删除银行卡
                             */}
                            {isModify && !security.isMarscoAuditing ? (
                                <img
                                    className="delete"
                                    onClick={() => {
                                        handleDelete(item);
                                    }}
                                    src={deleteIcon}
                                    alt="delete"
                                />
                            ) : (
                                <img src={arrowIcon} className="arrow" alt="" />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AchEnabledBankCardList;
